import { faListCheck } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { IconCircle } from 'components/UI/IconCircle/IconCircle';
import { PromptModal } from 'components/UI/PromptModal/PromptModal';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getEvaluationServices, getServices } from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { ReportEvaluationResult } from 'types/Evaluation';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Pet } from 'types/Pet';
import { DAYCARE_DURATION_OPTIONS, Service } from 'types/Service';
import { clearOnlineBookingDaycareAvailability } from 'actions/onlineBooking/actions/OnlineBookingAvailabilityActions';
import {
    navigateToStep,
    removeOnlineBookingBookingItems,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingDaycareItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    isOnlineBookingItemEmpty,
    OnlineBookingPricingOption,
    OnlineBookingStep
} from 'types/OnlineBooking';
import {
    formatServicePriceLabel,
    getAvailableServicesForPet,
    isDaycareServiceAvailableForPets,
    isServiceAvailableForPet
} from 'utils/serviceUtils';
import classes from './OnlineBookingDaycareServiceListStep.module.scss';

const getServicePriceLabel = (
    price: number,
    service: Service,
    pricingOption: OnlineBookingPricingOption,
    pet?: Pet
) => {
    if (service.daycareMetadata?.durationOption === DAYCARE_DURATION_OPTIONS.HOURLY) {
        const hourlyRate =
            service.fixedPrice.find(price => price.petType?.id === pet?.type?.id)?.price || 0;

        return formatServicePriceLabel(hourlyRate, pricingOption, ' / hour');
    }

    return formatServicePriceLabel(price, pricingOption);
};

export const OnlineBookingDaycareServiceListStep: React.FC = React.memo(() => {
    const marketplace = useMarketplace();

    const services = useSelector(getServices);
    const evaluationServices = useSelector(getEvaluationServices);
    const daycareBookingItems = useSelector(getOnlineBookingDaycareItems);

    const [showExitConfirmationModal, setShowExitConfirmationModal] = React.useState(false);
    const [showRemoveInvalidPetsModal, setShowRemoveInvalidPetsModal] = React.useState(false);
    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        daycareBookingItems[0].uuid
    );

    const hasDaycareEvaluation = React.useMemo(() => {
        return (
            marketplace.evaluationEnabled &&
            evaluationServices.some(
                service =>
                    service.evaluationMetadata?.businessAreas.includes(
                        MarketplaceServiceCategory.DAYCARE
                    )
            )
        );
    }, [evaluationServices, marketplace.evaluationEnabled]);

    // Pet categorization based on evaluation status
    const eligiblePets = React.useMemo(() => {
        if (!hasDaycareEvaluation) {
            return daycareBookingItems.filter(item => {
                return (
                    services
                        .filter(
                            service => service.businessArea === MarketplaceServiceCategory.DAYCARE
                        )
                        .filter(service => isServiceAvailableForPet(service, item.pet)).length > 0
                );
            });
        }

        return daycareBookingItems.filter(
            item => item.pet.evaluationReport?.daycareResult === ReportEvaluationResult.PASS
        );
    }, [daycareBookingItems, hasDaycareEvaluation, services]);

    const nonEligiblePets = React.useMemo(() => {
        if (!hasDaycareEvaluation) {
            return daycareBookingItems.filter(item => {
                return (
                    services
                        .filter(
                            service => service.businessArea === MarketplaceServiceCategory.DAYCARE
                        )
                        .filter(service => isServiceAvailableForPet(service, item.pet)).length === 0
                );
            });
        }

        return daycareBookingItems.filter(
            item => item.pet.evaluationReport?.daycareResult === ReportEvaluationResult.FAIL
        );
    }, [services, daycareBookingItems, hasDaycareEvaluation]);

    const petsRequiringEvaluation = React.useMemo(() => {
        if (!hasDaycareEvaluation) return [];

        return daycareBookingItems.filter(
            item =>
                !item.pet.evaluationReport ||
                item.pet.evaluationReport?.daycareResult === ReportEvaluationResult.FAIL
        );
    }, [daycareBookingItems, hasDaycareEvaluation]);

    const selectedBookingItem = React.useMemo(() => {
        return daycareBookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [daycareBookingItems, selectedBookingItemUuid]);

    const eligibleBookingItemUuids = React.useMemo(() => {
        return new Set(eligiblePets.map(item => item.uuid));
    }, [eligiblePets]);

    const selectedBookingItemIsEligible = React.useMemo(() => {
        return eligibleBookingItemUuids.has(selectedBookingItemUuid);
    }, [eligibleBookingItemUuids, selectedBookingItemUuid]);

    const selectedPetRequiresEvaluation = React.useMemo(() => {
        return (
            hasDaycareEvaluation &&
            petsRequiringEvaluation.some(item => item.uuid === selectedBookingItemUuid)
        );
    }, [hasDaycareEvaluation, petsRequiringEvaluation, selectedBookingItemUuid]);

    const sharedSelectedService = React.useMemo(() => {
        if (eligiblePets.length === 0) return undefined;

        const selectedServiceId = eligiblePets[0].service?.id;

        if (!selectedServiceId) return undefined;

        if (!eligiblePets.every(item => item.service?.id === selectedServiceId)) {
            return undefined;
        }

        return eligiblePets[0].service;
    }, [eligiblePets]);

    const hasSharedSelectedService = !!sharedSelectedService?.id;

    const isValid = React.useMemo(() => {
        return eligiblePets.length > 0 && hasSharedSelectedService;
    }, [eligiblePets.length, hasSharedSelectedService]);

    const eligibleSelectedPets = React.useMemo(() => {
        return eligiblePets.map(item => item.pet);
    }, [eligiblePets]);

    const invalidBookingItems = React.useMemo(() => {
        const invalidByUuid = new Map<string, (typeof daycareBookingItems)[number]>();

        for (const item of [...petsRequiringEvaluation, ...nonEligiblePets]) {
            invalidByUuid.set(item.uuid, item);
        }

        return Array.from(invalidByUuid.values());
    }, [petsRequiringEvaluation, nonEligiblePets]);

    const invalidBookingItemUuids = React.useMemo(() => {
        return new Set(invalidBookingItems.map(item => item.uuid));
    }, [invalidBookingItems]);

    const removeInvalidPetsModalDescription = React.useMemo(() => {
        const renderBoldNames = (names: Array<string>) => {
            return names.map((name, index) => {
                const isLast = index === names.length - 1;
                const isSecondToLast = index === names.length - 2;
                const separator = isLast ? '' : isSecondToLast && names.length > 1 ? ' and ' : ', ';

                return (
                    <React.Fragment key={name + index}>
                        <strong>{name}</strong>
                        {separator}
                    </React.Fragment>
                );
            });
        };

        const requiringEvalUuids = new Set(petsRequiringEvaluation.map(item => item.uuid));
        const onlyRequireEvaluation = invalidBookingItems.every(item =>
            requiringEvalUuids.has(item.uuid)
        );
        const onlyHaveNoServices = invalidBookingItems.every(
            item => !requiringEvalUuids.has(item.uuid)
        );
        const names = renderBoldNames(invalidBookingItems.map(item => item.pet.name));

        if (onlyRequireEvaluation) {
            return (
                <>{names} need to pass an evaluation first. You can book them separately later.</>
            );
        }

        if (onlyHaveNoServices) {
            return <>No services are available for {names}. You can book them separately later.</>;
        }

        return <>{names} can&apos;t be booked here. You can book them separately later.</>;
    }, [invalidBookingItems, petsRequiringEvaluation]);

    const sharedAvailableServices = React.useMemo(() => {
        if (!selectedBookingItemIsEligible || !selectedBookingItem?.pet) {
            return [];
        }

        return getAvailableServicesForPet(
            services,
            selectedBookingItem.pet,
            MarketplaceServiceCategory.DAYCARE
        ).filter(item => isDaycareServiceAvailableForPets(item.service, eligibleSelectedPets));
    }, [eligibleSelectedPets, selectedBookingItem, selectedBookingItemIsEligible, services]);

    const serviceChangeHandler = (service: Service) => {
        const updatedItems = daycareBookingItems.map(item => ({
            ...item,
            service: eligibleBookingItemUuids.has(item.uuid) ? service : item.service,
            days: [],
            dropOffTime: undefined,
            pickUpTime: undefined
        }));

        dispatch(clearOnlineBookingDaycareAvailability());
        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const backHandler = React.useCallback(() => {
        const emptyBookingItems = daycareBookingItems.filter(isOnlineBookingItemEmpty);

        if (emptyBookingItems.length === daycareBookingItems.length) {
            dispatch(removeOnlineBookingBookingItems(daycareBookingItems.map(item => item.uuid)));

            return dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
        }

        setShowExitConfirmationModal(true);
    }, [daycareBookingItems]);

    const exitConfirmationModalConfirmHandler = React.useCallback(() => {
        dispatch(removeOnlineBookingBookingItems(daycareBookingItems.map(item => item.uuid)));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [daycareBookingItems]);

    const submitHandler = () => {
        if (!hasSharedSelectedService) {
            return;
        }

        if (invalidBookingItems.length > 0) {
            setShowRemoveInvalidPetsModal(true);
            return;
        }

        return dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    const removeInvalidPetsConfirmHandler = () => {
        dispatch(removeOnlineBookingBookingItems(invalidBookingItems.map(item => item.uuid)));
        setShowRemoveInvalidPetsModal(false);
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <div className={classes.petTabs}>
                    {daycareBookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={
                                eligibleBookingItemUuids.has(item.uuid)
                                    ? hasSharedSelectedService
                                    : !!item.service?.id
                            }
                            warning={invalidBookingItemUuids.has(item.uuid)}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                {selectedBookingItemIsEligible && sharedAvailableServices.length === 0 && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no daycare services available for all selected pets. Please contact the business for assistance."
                    />
                )}

                {!selectedBookingItemIsEligible && !selectedPetRequiresEvaluation && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no services available for your pet. Please contact the business for assistance."
                    />
                )}

                {selectedPetRequiresEvaluation && (
                    <div className="flex flex-1 flex-col items-center justify-center text-center gap-3">
                        <IconCircle size={72} icon={faListCheck} />

                        <h2 className="h3 w-full">This pet needs to pass an evaluation</h2>

                        <p className="text-md mx-auto max-w-sm">
                            Please contact the business to arrange your appointment.
                        </p>
                    </div>
                )}

                {selectedBookingItemIsEligible &&
                    sharedAvailableServices.length > 0 &&
                    !selectedPetRequiresEvaluation && (
                        <>
                            <h1 className="h3 w-full">Daycare Services</h1>

                            <div className="flex flex-col gap-3">
                                {sharedAvailableServices.map(item => (
                                    <RadioCard
                                        key={item.service.id}
                                        title={item.service.name}
                                        content={item.service.description}
                                        description={getServicePriceLabel(
                                            item.price,
                                            item.service,
                                            marketplace.onlineBookingPreferences.pricingOption,
                                            selectedBookingItem?.pet
                                        )}
                                        checked={sharedSelectedService?.id === item.service.id}
                                        onChange={() => serviceChangeHandler(item.service)}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>

            {showExitConfirmationModal && (
                <PromptModal
                    open={true}
                    title="Exit Daycare Services"
                    description="Closing now will discard any unsaved changes. Do you want to exit?"
                    onConfirm={exitConfirmationModalConfirmHandler}
                    onCancel={() => setShowExitConfirmationModal(false)}
                />
            )}

            {showRemoveInvalidPetsModal && (
                <PromptModal
                    open={true}
                    title="Remove these pets from your booking?"
                    description={removeInvalidPetsModalDescription}
                    onConfirm={removeInvalidPetsConfirmHandler}
                    onCancel={() => setShowRemoveInvalidPetsModal(false)}
                />
            )}
        </TwoColumnsLayout>
    );
});

OnlineBookingDaycareServiceListStep.displayName = 'OnlineBookingDaycareServiceListStep';
